/**
 * 神灯掉落表（cfg_lamp_drop）的前端形状。判法以服务端 src/game/lamp-drops.ts 为准（写接口与发布闸都在那边拦），
 * 这里只放页面要用的形状、选项与新行模板。
 *
 * 《Fast Ludo 棋盘格位表 v4.2》「神灯掉落表」：表结构定死，内容由运营填；按相对权重开 1 行、即时到账。
 * A 期（2026-09-29）只结算金币 / 体力；万能卡、物品卡、活动道具可以先填好，发布时有权重会被拦（下一期开放）。
 */
export const LAMP_KINDS = ["coin", "dice", "wildcard", "item_card", "act_item"] as const;
export type LampKind = (typeof LAMP_KINDS)[number];
/** 会结算的种类（与服务端 LAMP_SETTLED_KINDS 同步；B 期起五种全开） */
export const LAMP_SETTLED: readonly LampKind[] = ["coin", "dice", "wildcard", "item_card", "act_item"];
/** 物品卡一次最多几张（与服务端 LAMP_MAX_ITEM_CARDS 同步：卡码要预抽好钉进发奖命令） */
export const LAMP_MAX_ITEM_CARDS = 64;

export interface LampDrop {
  drop_id: string;
  kind: LampKind;
  value: number;
  ref: string | null;
  weight: number;
  pity_eligible: boolean;
  fallback_kind: "coin" | "dice" | null;
  fallback_value: number | null;
  enabled: boolean;
  note?: string;
}

/**
 * 产品表的结构（v4.2 截图：白格定死、黄格运营填；与服务端 LAMP_FIXED_ROWS 同步）。
 * LAMP_001–005 的类型与「是否计入万能卡保底」定死；LAMP_006–008 是预留行，全部字段运营填。
 */
export const LAMP_FIXED: ReadonlyArray<{ drop_id: string; kind: LampKind; pity_eligible: boolean }> = [
  { drop_id: "LAMP_001", kind: "wildcard", pity_eligible: true },
  { drop_id: "LAMP_002", kind: "coin", pity_eligible: false },
  { drop_id: "LAMP_003", kind: "dice", pity_eligible: false },
  { drop_id: "LAMP_004", kind: "item_card", pity_eligible: false },
  { drop_id: "LAMP_005", kind: "act_item", pity_eligible: false },
];
export const LAMP_FREE = ["LAMP_006", "LAMP_007", "LAMP_008"] as const;
export const isFixedRow = (id: string): boolean => LAMP_FIXED.some((f) => f.drop_id === id);

/**
 * 补齐成产品表的 8 行（按编号顺序）：库里有的行原样用，缺的按模板补一个占位（权重 0、停用）。
 * 加载时对基线与编辑态用同一份，所以打开页面不会凭空显示「有改动」。
 */
export function withTemplate(drops: readonly LampDrop[]): LampDrop[] {
  const byId = new Map(drops.map((d) => [d.drop_id, d]));
  const blank = (drop_id: string, kind: LampKind, pity: boolean): LampDrop =>
    ({ drop_id, kind, value: 1, ref: null, weight: 0, pity_eligible: pity, fallback_kind: null, fallback_value: null, enabled: false, note: "" });
  return [
    ...LAMP_FIXED.map((f) => byId.get(f.drop_id) ?? blank(f.drop_id, f.kind, f.pity_eligible)),
    ...LAMP_FREE.map((id) => byId.get(id) ?? blank(id, "coin", false)),
  ];
}

/** 换种类时把只属于别的种类的字段清掉（引用只给物品卡 / 活动道具，回退只给活动道具） */
export function switchKind(d: LampDrop, kind: LampKind): LampDrop {
  return {
    ...d, kind,
    ref: kind === "item_card" || kind === "act_item" ? d.ref : null,
    fallback_kind: kind === "act_item" ? (d.fallback_kind ?? "coin") : null,
    fallback_value: kind === "act_item" ? (d.fallback_value ?? 1) : null,
    // 只在预留行（006–008）上会换种类；保底勾选跟着种类走，切走再切回不留残值
    pity_eligible: kind === "wildcard",
  };
}

/** 页面上的即时提示（服务端才是准的）：每行一个问题或 null */
export function lampRowIssue(d: LampDrop, all: readonly LampDrop[]): string | null {
  void all;
  if (!Number.isInteger(d.value) || d.value < 1) return "lamp.issue.value";
  const live = d.enabled && d.weight > 0;
  if (live && (d.kind === "item_card" || d.kind === "act_item") && !d.ref) return "lamp.issue.ref";
  if (d.kind === "item_card" && d.value > LAMP_MAX_ITEM_CARDS) return "lamp.issue.cards";
  return null;
}
